import { useDraggable } from "@dnd-kit/core";
import { CalendarClock, GripVertical } from "lucide-react";
import { InstallationSchedule } from "../../types";
import { Badge } from "./Badge";
import { etichettaIpotesiData, isMesePlaceholder } from "../../utils/dataIpotesi";

/** Prefisso degli id draggable, speculare a DROP_GIORNO_PREFIX di CalendarBand. */
export const DRAG_RECORD_PREFIX = "record:";

/**
 * Tessera di un'installazione in attesa di data. Trascinabile solo se il
 * backend ha dichiarato il record modificabile (`can_edit`): chi ha accesso in
 * lettura non ha necessariamente quello in scrittura — il ruolo cliente vede le
 * installazioni delle sue aziende senza poterle toccare. Per quei record la
 * tessera resta cliccabile ma ferma, invece di offrire un trascinamento che
 * finirebbe in un 403.
 */
function Tessera({ record, onApri }: { record: InstallationSchedule; onApri: () => void }) {
  const trascinabile = record.can_edit === true;
  // "Settembre 2026" per chi porta un segnaposto di mese, null per chi non ha
  // nessuna data. `etichettaIpotesiData` renderebbe anche una data secca, ma qui
  // non ne arrivano: nella pila entrano solo i due casi senza giorno.
  const mese = isMesePlaceholder(record.ipotesi_data, record.stato)
    ? etichettaIpotesiData(record.ipotesi_data, record.stato)
    : null;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `${DRAG_RECORD_PREFIX}${record.id}`,
    data: { record },
    disabled: !trascinabile,
  });

  return (
    <div
      ref={setNodeRef}
      className={`flex items-start gap-2 rounded-card border border-line bg-white p-3 shadow-card transition-shadow ${
        isDragging ? "opacity-40" : "hover:shadow-md"
      }`}
    >
      {trascinabile && (
        // La maniglia è un elemento dedicato: se il drag stesse su tutta la
        // tessera, il click per aprire il dettaglio verrebbe intercettato dal
        // sensore al primo pixel di movimento del mouse.
        <button
          type="button"
          {...listeners}
          {...attributes}
          aria-label={`Trascina ${record.insegna ?? "installazione"} su un giorno del calendario`}
          className="-ml-1 shrink-0 cursor-grab touch-none rounded p-1 text-ink/25 transition-colors hover:bg-black/5 hover:text-ink/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      <button type="button" onClick={onApri} className="min-w-0 flex-1 text-left">
        <p className="truncate text-sm font-semibold text-ink">{record.insegna ?? "Senza insegna"}</p>
        <p className="truncate text-xs text-ink/50">{record.localita ?? "—"}</p>
        {/* Il mese in cui va programmata, per chi un mese ce l'ha. È l'unica
            informazione che distingue le due metà della pila: senza,
            un'installazione da fare a settembre e una di cui non si sa niente
            avrebbero lo stesso aspetto. Assente per chi non ha nessuna data —
            lì non c'è niente da dire, e una riga vuota direbbe il contrario. */}
        {mese && (
          <p className="mt-1 flex items-center gap-1 text-xs font-medium text-primary">
            <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {mese}
          </p>
        )}
        <div className="mt-2 flex flex-wrap gap-1">
          <Badge stato={record.stato}>{record.stato}</Badge>
          {record.in_ritardo && <Badge color="danger">In ritardo</Badge>}
          {record.attivazione_mancante && <Badge color="warning">Attivazione da creare</Badge>}
        </div>
      </button>
    </div>
  );
}

/**
 * Riquadro "Da pianificare": le installazioni che un giorno vero non ce l'hanno
 * ancora, impilate in verticale accanto al calendario invece che in una fila
 * orizzontale a scorrimento. Verticale perché è la disposizione che permette di
 * vedere insieme la pila e la fascia calendario, che è il presupposto del
 * trascinamento.
 *
 * Ne contiene DUE tipi: quelle senza alcuna data, e quelle che portano solo un
 * mese (`ipotesi_data` al giorno 1 — vedi `utils/dataIpotesi.ts`). Le seconde
 * stanno qui e non nella fascia perché disegnare una tessera dentro una
 * cella-giorno afferma che l'appuntamento è quel giorno, ed è falso: la
 * posizione dice più di qualunque etichetta le si metta accanto. La tessera
 * dichiara il mese, e trascinarla resta il gesto che le dà la data vera.
 */
export function DaPianificare({
  records,
  onApri,
  isLoading,
}: {
  records: InstallationSchedule[];
  onApri: (r: InstallationSchedule) => void;
  isLoading?: boolean;
}) {
  return (
    <section>
      <h2 className="mb-3 flex items-baseline gap-2 text-sm font-bold uppercase tracking-wide text-ink/50">
        Da pianificare
        {!isLoading && records.length > 0 && (
          <span className="tabular-nums font-semibold text-ink/40">({records.length})</span>
        )}
      </h2>

      {isLoading ? (
        <p className="text-sm text-ink/40">Caricamento…</p>
      ) : records.length === 0 ? (
        <p className="text-sm text-ink/40">Nessuna installazione in attesa di un giorno.</p>
      ) : (
        <div className="flex max-h-[560px] flex-col gap-2 overflow-y-auto pr-1">
          {records.map((r) => (
            <Tessera key={r.id} record={r} onApri={() => onApri(r)} />
          ))}
        </div>
      )}
    </section>
  );
}
